// motifGlyphs.jsx
// -----------------------------------------------------------------------------
// Inline-SVG decorative glyphs, keyed by `glyph` field on affectPalette.js.
//
// Each glyph is rendered at ~120px in the top-right corner of the panel at
// low opacity (CSS-controlled, ~6–8%). They're intentionally abstract /
// quiet — affect-tinted decoration, not pictograms. Iterate freely.
//
// All glyphs accept { color, size, ...rest } and render a single <svg>. Color
// drives `stroke` and `fill` where appropriate (see per-glyph notes).
//
// Author seed glyphs — abstract calligraphic / botanical / type-derived shapes
// drawn by hand in Cypher-friendly viewBox coords. Replace any that don't feel
// right; the registry is the source of truth and HoverPanelMockup looks up
// glyphs by key, so swapping is one-file.
//
// Spec reference: docs/journal/PHASE_4D_VA_HOVER_PANEL_VISUAL_DESIGN_MOCKUP.md
// -----------------------------------------------------------------------------

import React from 'react';

// Corner glyphs share one stroke contract — kept hairline-thin so they
// read as quiet decoration alongside the editorial header and pulled
// quote, not as a competing focal point. Earlier we used strokeWidth 1.8
// which (paired with the 0.5 CSS opacity) still read as a heavy ink line;
// 0.9 matches the chapter activity sparkline's stroke width, giving the
// whole panel one consistent hairline weight.
const baseProps = (color, size) => ({
  width: size,
  height: size,
  viewBox: '0 0 100 100',
  xmlns: 'http://www.w3.org/2000/svg',
  fill: 'none',
  stroke: color,
  strokeWidth: 0.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
});


// Strategic — a stylised ampersand, the typographic emblem of "and-then, also,
// in addition" — the conjunctional brain.
const Ampersand = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 35 78 C 18 70, 22 48, 42 42 C 58 38, 60 28, 50 22 C 38 16, 26 28, 38 42 C 50 56, 70 70, 82 78" />
    <path d="M 70 60 C 78 62, 85 58, 86 50" opacity="0.7" />
  </svg>
);

// Grouchy — a thorny branch fragment, weathered but not violent.
const ThornBranch = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 18 78 C 38 70, 58 60, 82 22" />
    <path d="M 30 70 L 24 60" />
    <path d="M 45 60 L 52 50" />
    <path d="M 60 50 L 54 42" />
    <path d="M 72 36 L 80 40" />
    <path d="M 38 64 L 32 70" opacity="0.6" />
  </svg>
);

// Frustrated — a curl of smoke rising. Reads warmer than literal-flame.
const Flame = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 50 82 C 35 70, 40 58, 50 50 C 60 42, 56 32, 48 22 C 60 30, 70 42, 62 58 C 56 70, 64 78, 54 82" />
    <path d="M 38 76 C 32 70, 36 60, 42 56" opacity="0.5" />
  </svg>
);

// Supportive — a soft leaf shape; warm, grounded.
const Leaf = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 22 78 C 28 40, 60 22, 82 22 C 78 56, 56 78, 22 78 Z" />
    <path d="M 22 78 C 40 60, 60 42, 82 22" opacity="0.6" />
  </svg>
);

// Informative — a quill nib seen edge-on. Library-quiet.
const Quill = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 22 82 L 78 26" />
    <path d="M 22 82 L 30 74 L 38 78 Z" />
    <path d="M 60 32 L 78 26 L 72 44 Z" />
    <path d="M 38 66 L 56 48" opacity="0.5" />
  </svg>
);

// Teasing — a curl / spiral. A sly half-smile in shape form.
const Curl = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 50 50 m -20 0 a 20 20 0 1 0 40 0 a 20 20 0 1 0 -40 0" opacity="0.4" />
    <path d="M 50 50 m -12 0 a 12 12 0 1 0 24 0 a 12 12 0 1 0 -24 0" opacity="0.7" />
    <path d="M 50 50 m -6 0 a 6 6 0 1 0 12 0 a 6 6 0 1 0 -12 0" />
    <path d="M 70 30 C 78 28, 84 34, 82 42" />
  </svg>
);

// Apologetic — a single feather; soft, retreating.
const Feather = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 78 22 C 60 30, 40 50, 28 78" />
    <path d="M 70 32 L 50 38" opacity="0.6" />
    <path d="M 60 44 L 40 50" opacity="0.6" />
    <path d="M 50 56 L 32 64" opacity="0.6" />
    <path d="M 40 68 L 26 76" opacity="0.6" />
  </svg>
);

// Testy — small sparks. Short-fused but still social.
const Sparks = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 30 50 L 40 50 M 35 45 L 35 55 M 32 47 L 38 53 M 32 53 L 38 47" />
    <path d="M 60 30 L 70 30 M 65 25 L 65 35 M 62 27 L 68 33 M 62 33 L 68 27" opacity="0.7" />
    <path d="M 70 70 L 78 70 M 74 66 L 74 74" opacity="0.5" />
  </svg>
);

// Relieved — a single soft wave. Exhaled tension.
const Wave = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <path d="M 14 56 C 28 48, 40 64, 54 56 C 68 48, 80 64, 90 56" />
    <path d="M 14 68 C 28 60, 40 76, 54 68 C 68 60, 80 76, 90 68" opacity="0.55" />
  </svg>
);

// Neutral / thin-card — a single open circle. Quiet, undefined.
const Circle = ({ color = '#000', size = 120, ...rest }) => (
  <svg {...baseProps(color, size)} {...rest}>
    <circle cx="50" cy="50" r="28" />
    <circle cx="50" cy="50" r="18" opacity="0.5" />
  </svg>
);

const REGISTRY = {
  ampersand: Ampersand,
  thornBranch: ThornBranch,
  flame: Flame,
  leaf: Leaf,
  quill: Quill,
  curl: Curl,
  feather: Feather,
  sparks: Sparks,
  wave: Wave,
  circle: Circle,
};

/**
 * Get a glyph React component by registry key. Falls back to `Circle` for any
 * unknown key (matches affectPalette.js's neutral fallback).
 */
export function getMotifGlyph(key) {
  return REGISTRY[key] || Circle;
}

export default REGISTRY;

// ---------------------------------------------------------------------------
// Background motifs
// ---------------------------------------------------------------------------
// A second, parallel registry for *large* decorative shapes that sit in the
// panel-background slot (currently: top of `.aai-cc-panel__surface`, 70px
// inset from the left). These have a different contract than the corner
// motif glyphs above:
//
//   - Drawn with `fill` (filled blobs), not `stroke` (outline glyphs).
//   - Sized by width-in-px; height is computed from the SVG's natural aspect.
//   - Color defaults to the affect accent, but each blob accepts a
//     `color` override so it can also be rendered as a fixed-color motif
//     (e.g. the original literal green, if we want it).
//
// The split keeps the corner-glyph rendering contract clean while still
// letting the design language treat *both* kinds of decoration as motifs.
// ---------------------------------------------------------------------------

const bgBaseProps = (width, viewBox) => ({
  width,
  // height auto-derived by preserving aspect ratio inside the parent box
  viewBox,
  xmlns: 'http://www.w3.org/2000/svg',
  preserveAspectRatio: 'xMidYMin meet',
  'aria-hidden': true,
});

// Green blob — organic top-of-panel form. Source: src/assets/greenBlob.svg.
// Inlined here so it can be color-tinted via the `color` prop (the original
// SVG hard-codes #390 fill).
const GreenBlob = ({ color = '#3a9930', width = 270, ...rest }) => (
  <svg {...bgBaseProps(width, '0 0 281.95 222.63')} {...rest}>
    <path
      fill={color}
      d="M18.23,118.42C52.86,42.2,113.49-4.97,169.23.42c2.84.27,58.15,6.32,75,42,9.16,19.41-1.3,30.11,9,60,10.18,29.53,24.56,31.15,28,53,3.4,21.61-5.37,53.57-26,64-36.21,18.31-72.74-48.07-142-39-27.53,3.6-37.86,16.2-63,10C25.57,184.33-2.88,162.72.23,141.42c1.62-11.08,11.3-18.8,18-23Z"
    />
  </svg>
);

const BACKGROUND_REGISTRY = {
  greenBlob: GreenBlob,
};

/**
 * Get a background-motif React component by registry key. Returns `null` for
 * unknown keys so the caller can short-circuit the render (background motifs
 * are optional, unlike corner glyphs which always render).
 */
export function getBackgroundMotif(key) {
  return BACKGROUND_REGISTRY[key] || null;
}
